// pages/eventos/admin/SorteiosAdminEventoPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../axiosConfig";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  Gift,
  ThumbsUp,
  Trophy,
} from "lucide-react";
import "./SorteiosAdminEventoPage.css";

const SorteiosAdminEventoPage = () => {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get("slug");

  const [sorteios, setSorteios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [aprovandoId, setAprovandoId] = useState(null);

  useEffect(() => {
    axios
      .get("/admin/sorteios")
      .then((res) => {
        const todos = Array.isArray(res.data) ? res.data : [];
        const filtrados = slug
          ? todos.filter((s) => s.evento_slug === slug)
          : todos;

        setSorteios(filtrados);
      })
      .catch(() => setErro("Erro ao carregar sorteios."))
      .finally(() => setLoading(false));
  }, [slug]);

  const aprovar = async (sorteio) => {
    setAprovandoId(sorteio.usuario_id);

    try {
      const res = await axios.post("/admin/aprovar-premio", {
        usuario_id: sorteio.usuario_id,
        evento_id: sorteio.evento_id,
        evento_slug: sorteio.evento_slug,
        premio: sorteio.premio,
      });

      setSorteios((prev) =>
        prev.map((s) =>
          s.usuario_id === sorteio.usuario_id
            ? {
                ...s,
                status_evento:
                  res.data?.tipo === "fisico"
                    ? "resgate_criado"
                    : "pontos_creditados",
              }
            : s
        )
      );
    } catch {
      alert("Erro ao aprovar prêmio.");
    } finally {
      setAprovandoId(null);
    }
  };

  const exportarExcel = () => {
    const planilha = sorteios.map((s) => ({
      Nome: s.nome_usuario,
      Email: s.email,
      Telefone: s.telefone,
      Empresa: s.nome_empresa || "-",
      Evento: s.evento_slug,
      Prêmio: s.premio,
      Status: s.status_evento?.replaceAll("_", " "),
      Data: new Date(s.data_participacao).toLocaleString("pt-BR"),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(planilha);

    XLSX.utils.book_append_sheet(wb, ws, "Sorteios");
    XLSX.writeFile(wb, `sorteios-${slug || "todos"}-${Date.now()}.xlsx`);
  };

  const resumo = useMemo(() => {
    return {
      total: sorteios.length,
      pendentes: sorteios.filter((s) =>
        ["em_analise", "aguardando_aprovacao"].includes(s.status_evento)
      ).length,
      pontos: sorteios.filter((s) => s.status_evento === "pontos_creditados")
        .length,
      resgates: sorteios.filter((s) => s.status_evento === "resgate_criado")
        .length,
    };
  }, [sorteios]);

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const formatarData = (data) => {
    if (!data) return "—";

    try {
      return new Date(data).toLocaleString("pt-BR");
    } catch {
      return "—";
    }
  };

  const getStatusLabel = (status) =>
    status ? status.replaceAll("_", " ") : "sem status";

  return (
    <div className="sorteiosEventoAdmin-page">
      <div className="sorteiosEventoAdmin-topbar">
        <div>
          <span className="sorteiosEventoAdmin-kicker">
            Eventos promocionais
          </span>

          <h2>{slug ? `Sorteios do Evento: ${slug}` : "Todos os Sorteios"}</h2>

          <p>
            Visualize os sorteios vinculados ao evento selecionado, acompanhe o
            status dos prêmios e aprove pendências diretamente pela listagem.
          </p>
        </div>

        <button
          type="button"
          onClick={exportarExcel}
          className="sorteiosEventoAdmin-btn primary"
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      {erro && <div className="sorteiosEventoAdmin-alert danger">{erro}</div>}

      <div className="sorteiosEventoAdmin-cards">
        <div className="sorteiosEventoAdmin-card">
          <span>Total de sorteios</span>
          <strong>{formatarNumero(resumo.total)}</strong>
        </div>

        <div className="sorteiosEventoAdmin-card warning">
          <span>Pendentes</span>
          <strong>{formatarNumero(resumo.pendentes)}</strong>
        </div>

        <div className="sorteiosEventoAdmin-card success">
          <span>Pontos creditados</span>
          <strong>{formatarNumero(resumo.pontos)}</strong>
        </div>

        <div className="sorteiosEventoAdmin-card info">
          <span>Resgates criados</span>
          <strong>{formatarNumero(resumo.resgates)}</strong>
        </div>
      </div>

      <div className="sorteiosEventoAdmin-tableCard">
        <div className="sorteiosEventoAdmin-tableHeader">
          <div>
            <h3>Lista de sorteios</h3>
            <span>
              {slug
                ? "Resultado filtrado pelo evento informado na URL."
                : "Exibindo todos os sorteios disponíveis."}
            </span>
          </div>

          <Trophy size={18} />
        </div>

        {loading ? (
          <div className="sorteiosEventoAdmin-loading">
            Carregando sorteios...
          </div>
        ) : sorteios.length === 0 ? (
          <div className="sorteiosEventoAdmin-emptyBox">
            Nenhum sorteio encontrado para este evento.
          </div>
        ) : (
          <div className="sorteiosEventoAdmin-tableWrapper">
            <table className="sorteiosEventoAdmin-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Contato</th>
                  <th>Empresa</th>
                  <th>Prêmio</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th>Ação</th>
                </tr>
              </thead>

              <tbody>
                {sorteios.map((s) => (
                  <tr key={`${s.usuario_id}-${s.evento_id}`}>
                    <td>
                      <div className="sorteiosEventoAdmin-cellMain">
                        <strong>{s.nome_usuario || "—"}</strong>
                        <span>ID usuário #{s.usuario_id || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="sorteiosEventoAdmin-cellMain">
                        <span>{s.email || "—"}</span>
                        <span>{s.telefone || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="sorteiosEventoAdmin-cellMain">
                        <strong>{s.nome_empresa || "—"}</strong>
                        <span>{s.evento_slug || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <span className="sorteiosEventoAdmin-premio">
                        <Gift size={13} />
                        {s.premio || "—"}
                      </span>
                    </td>

                    <td>
                      <div className="sorteiosEventoAdmin-cellMain">
                        <span>{formatarData(s.data_participacao)}</span>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`sorteiosEventoAdmin-status status-${s.status_evento}`}
                      >
                        {getStatusLabel(s.status_evento)}
                      </span>
                    </td>

                    <td>
                      {["em_analise", "aguardando_aprovacao"].includes(
                        s.status_evento
                      ) ? (
                        <button
                          type="button"
                          className="sorteiosEventoAdmin-actionBtn approve"
                          disabled={aprovandoId === s.usuario_id}
                          onClick={() => aprovar(s)}
                        >
                          {aprovandoId === s.usuario_id ? (
                            <>
                              <Clock size={14} />
                              Aprovando...
                            </>
                          ) : (
                            <>
                              <ThumbsUp size={14} />
                              Aprovar
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="sorteiosEventoAdmin-approved">
                          <CheckCircle2 size={14} />
                          Processado
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SorteiosAdminEventoPage;